/**
 * Lógica de las ventanas de tiempo.
 *
 * Separada del componente y sin JSX para poder probarla con el ejecutor de
 * tests de Node, que no carga archivos .tsx. Es también donde corresponde:
 * decidir qué ventana ofrecer es una regla, no una vista.
 */

/**
 * Las ventanas largas existen porque hay con qué responderlas: el rollup
 * diario guarda más de un año de totales. Sin ellas la pregunta que justifica
 * el producto —«¿el abandono empeoró respecto del trimestre pasado?»— no se
 * puede ni formular desde la pantalla.
 */
export const VENTANAS = [
  { etiqueta: 'Últimos 15 minutos', minutos: 15 },
  { etiqueta: 'Última hora', minutos: 60 },
  { etiqueta: 'Últimas 24 horas', minutos: 60 * 24 },
  { etiqueta: 'Últimos 7 días', minutos: 60 * 24 * 7 },
  { etiqueta: 'Últimos 30 días', minutos: 60 * 24 * 30 },
  { etiqueta: 'Últimos 90 días', minutos: 60 * 24 * 90 },
  { etiqueta: 'Último año', minutos: 60 * 24 * 365 },
] as const;

export function desdeISO(minutos: number): string {
  return new Date(Date.now() - minutos * 60_000).toISOString();
}

export interface Sugerencia {
  /** Ventana más chica que alcanzaría a incluir el último dato. */
  minutos: number;
  /** Frase completa, ya concordada. El prefijo no puede ir aparte: no hay uno
   *  que sirva para «Última hora» y para «Último año» a la vez. */
  etiqueta: string;
}

/** ClickHouse entrega 'YYYY-MM-DD HH:MM:SS.mmm' en UTC. */
export function aFecha(marca: string): Date {
  return new Date(`${marca.replace(' ', 'T')}Z`);
}

export function antiguedadEnMinutos(ultimo: string, ahora = Date.now()): number {
  return Math.max(0, Math.round((ahora - aFecha(ultimo).getTime()) / 60_000));
}

/**
 * Ventana mínima que incluiría el último dato. Devuelve null si la ventana
 * actual ya lo cubre —el vacío es real— o si ni la más amplia alcanza.
 */
export function sugerirVentana(
  ultimo: string,
  ventanaActual: number,
  ahora = Date.now()
): Sugerencia | null {
  const antiguedad = antiguedadEnMinutos(ultimo, ahora);
  if (antiguedad <= ventanaActual) return null;

  // Un poco de margen: si el dato tiene 61 minutos, "1 hora" no lo agarra.
  const necesaria = antiguedad + 1;
  const opcion = VENTANAS.find((v) => v.minutos >= necesaria);
  return opcion ? { minutos: opcion.minutos, etiqueta: opcion.etiqueta } : null;
}

export function describirAntiguedad(minutos: number): string {
  if (minutos < 1) return 'hace menos de un minuto';
  if (minutos < 60) return `hace ${minutos} ${minutos === 1 ? 'minuto' : 'minutos'}`;
  const horas = Math.round(minutos / 60);
  if (horas < 48) return `hace ${horas} ${horas === 1 ? 'hora' : 'horas'}`;
  return `hace ${Math.round(horas / 24)} días`;
}
